import orderBy from 'lodash/orderBy'

import {
  AccountFragment,
  PlanFragment,
  StripeSubscription,
  SubscriptionStatus,
} from 'src/graphql'
import {
  asSku,
  ELITE_PACKAGES,
  ELITE_PLUS_STRIPE_ID,
  PLAN_SKUS,
} from 'src/marketing/plans/constants'
import { Account, SubscriptionType } from 'src/user/fragments'

const subscriptionPriority: { [key: string]: number } = {
  Basic: 0,
  Enhanced: 1,
  Unlimited: 2,
  Enterprise: 3,
  Legacy: 4,
  'Affiliate Renewal': 5,
  'Elite Package': 6,
  'Elite Plus Package': 7,
}

/* If no name, look up SKU */
const planName = (name: string): string => {
  const sku = asSku(name)
  if (sku) {
    const plan = PLAN_SKUS[sku]
    return plan ? plan : name
  }
  return name
}

const sortSubscriptions = (
  subscriptions: SubscriptionType[],
): SubscriptionType[] => {
  return orderBy(subscriptions, [e => subscriptionPriority[e.name]], ['asc'])
}

const resolveBasicSubscription = (
  plan: PlanFragment,
  subscriptions: StripeSubscription[],
): Partial<SubscriptionType>[] =>
  plan &&
  (plan.title === 'Basic' || plan.title === 'Legacy') &&
  subscriptions.filter(s => s.name === 'Basic' || s.name === 'Legacy')
    .length === 0
    ? (subscriptions as Partial<SubscriptionType>[]).concat({
        id: plan.id,
        sku: 'limited',
        price: 0,
        interval: 'month',
        name: plan.title,
        status: SubscriptionStatus.ACTIVE,
      })
    : subscriptions

const getSubscriptions = (
  {
    groupSends,
    scheduledSends,
    loyaltyRewards,
    systemSends,
    heartfeltSends,
    plan,
  }: AccountFragment,
  subscriptions: StripeSubscription[],
): SubscriptionType[] =>
  sortSubscriptions(
    resolveBasicSubscription(plan, subscriptions)
      .filter(s => !s.sku || !ELITE_PACKAGES.includes(s.sku))
      .map(
        (subscription: Partial<SubscriptionType>): SubscriptionType =>
          ({
            ...subscription,
            name: planName(subscription.name ?? ''),
            features: null,
            // features: subscription.features
            //   ? Object.keys(subscription.features)
            //       .filter(
            //         key =>
            //           subscription.features &&
            //           (subscription.features as any)[key],
            //       )
            //       .reduce((obj: any, key: string) => {
            //         return {
            //           ...obj,
            //           [key]: subscriptionFeatures(
            //             groupSends,
            //             scheduledSends,
            //             loyaltyRewards,
            //             systemSends,
            //             heartfeltSends,
            //             key,
            //             subscription.features
            //               ? (subscription.features as any)[key]
            //               : 0,
            //           ),
            //         }
            //       }, {})
            //   : null,
          } as SubscriptionType),
      ),
  )

const getAddon = (
  systemSends: number,
  subscriptions: StripeSubscription[],
): SubscriptionType | null => {
  const addons = subscriptions.filter(s => ELITE_PACKAGES.includes(s.sku))
  if (addons.length > 0) {
    const { name, sub, count } = addons.reduce(
      ({ name, sub, count }, addon) => {
        const isElitePlus = addon.sku === ELITE_PLUS_STRIPE_ID
        return {
          name: isElitePlus ? addon.name : name,
          sub: isElitePlus ? addon : sub,
          count:
            addon &&
            addon.features &&
            typeof addon.features.systemSends === 'number'
              ? count + addon.features.systemSends
              : count,
        }
      },
      { name: 'Elite Package', sub: addons[0], count: 0 },
    )
    return {
      ...sub,
      name,
      features: {
        systemSends: {
          label: 'System Sends Remaining',
          remaining: systemSends,
          total: count,
        },
      },
    }
  }
  return null
}

export const getAccount = (account: AccountFragment): Account => ({
  ...account,
  subscriptions: getSubscriptions(account, account.subscriptions),
  addons: getAddon(account.systemSends, account.subscriptions),
})
